import React, { useState, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Textarea,
} from '@chakra-ui/react';

export const RichTextEditor = forwardRef(({
  htmlContent,
  textContent,
  onHtmlChange,
  onTextChange,
  onTabChange,
}, ref) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(null);
  const htmlEditorRef = useRef(null);
  const textEditorRef = useRef(null);

  useImperativeHandle(ref, () => ({
    insertAtCursor: (text) => {
      if (!cursorPosition) return;
      
      const { start, end } = cursorPosition;
      const activeRef = tabIndex === 0 ? htmlEditorRef : textEditorRef;
      const textarea = activeRef.current;
      if (!textarea) return;

      const value = textarea.value;
      const newContent = value.substring(0, start) + text + value.substring(end);
      
      if (tabIndex === 0) {
        onHtmlChange(newContent);
      } else {
        onTextChange(newContent);
      }

      // Need to wait for React to update the textarea value
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.selectionStart = start + text.length;
        textarea.selectionEnd = start + text.length;
      });
    }
  }));

  const handleTabChange = (index) => {
    setTabIndex(index);
    onTabChange?.(index);
  };

  const handleSelect = (e) => {
    setCursorPosition({
      start: e.target.selectionStart,
      end: e.target.selectionEnd
    });
  };

  return (
    <Box h="100%" display="flex" flexDirection="column">
      <Tabs 
        index={tabIndex} 
        onChange={handleTabChange}
        display="flex"
        flexDirection="column"
        h="100%"
        variant="enclosed"
      >
        <TabList>
          <Tab>HTML Version</Tab>
          <Tab>Text Version</Tab>
        </TabList>

        <TabPanels flex={1} display="flex" flexDirection="column">
          <TabPanel h="100%" p={0} display="flex" flexDirection="column">
            <Box h="100%">
              <Textarea
                ref={htmlEditorRef}
                value={htmlContent}
                onChange={(e) => onHtmlChange(e.target.value)}
                onSelect={handleSelect}
                onClick={handleSelect}
                onKeyUp={handleSelect}
                h="100%"
                resize="none"
                fontFamily="monospace"
                p={4}
                borderRadius="md"
                borderColor="gray.200"
                _focus={{
                  borderColor: "blue.500",
                  boxShadow: "outline",
                }}
                whiteSpace="pre"
                spellCheck="false"
              />
            </Box>
          </TabPanel>
          <TabPanel h="100%" p={0} display="flex" flexDirection="column">
            <Box h="100%">
              <Textarea
                ref={textEditorRef}
                value={textContent}
                onChange={(e) => onTextChange(e.target.value)}
                onSelect={handleSelect}
                onClick={handleSelect}
                onKeyUp={handleSelect}
                h="100%"
                resize="none"
                fontFamily="monospace"
                p={4}
                borderRadius="md"
                borderColor="gray.200"
                _focus={{
                  borderColor: "blue.500",
                  boxShadow: "outline",
                }}
              />
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
});
